import React, { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAuth } from "pages/context/AuthContext";
import { useAlert } from "pages/context/AlertContext";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import Draggable from "react-draggable";

function PaperComponent(props) {
  return (
    <Draggable
      handle=".draggable-objetivo-v2-faturas-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

function fmtData(value) {
  if (!value) return "";

  const s = String(value).slice(0, 10);
  const partes = s.split("-");

  if (partes.length !== 3) return s;

  return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

function fmtNumero(value, casas = 2) {
  const n = Number(value);

  if (!Number.isFinite(n)) return "";

  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

export default function DialogObjetivoV2DashboardFaturas({ open, onClose, idObjetivo }) {
  const { userData } = useAuth();
  const { showAlert } = useAlert();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [detalhe, setDetalhe] = useState(null);

  useEffect(() => {
    if (!open || !idObjetivo) {
      setDetalhe(null);
      return;
    }

    let ativo = true;

    const carregar = async () => {
      setLoading(true);

      try {
        const [data, err] = await handleApi(
          api.post("Comercial/SelObjetivoV2DashboardFaturas", {
            idobjetivo: idObjetivo,
            idutilizador: userData?.id || 0,
          })
        );

        if (!ativo) return;

        if (err) {
          setDetalhe(null);
          showAlert(err, "error");
          return;
        }

        setDetalhe(data || null);
      } finally {
        if (ativo) {
          setLoading(false);
        }
      }
    };

    carregar();

    return () => {
      ativo = false;
    };
  }, [open, idObjetivo, userData?.id, showAlert]);

  const clientes = useMemo(
    () => (Array.isArray(detalhe?.clientes) ? detalhe.clientes : []),
    [detalhe?.clientes]
  );

  const vendas = useMemo(
    () => (Array.isArray(detalhe?.faturas) ? detalhe.faturas : []),
    [detalhe?.faturas]
  );

  const columnsClientes = useMemo(
    () => [
      {
        Header: "Cliente",
        accessor: "cliente",
        width: 320,
        minWidth: 260,
      },
      {
        Header: "Ref",
        accessor: "refcliente",
        width: 130,
        minWidth: 120,
      },
      {
        Header: "NIF",
        accessor: "nif",
        width: 130,
        minWidth: 120,
      },
    ],
    []
  );

  const columnsVendas = useMemo(
    () => [
      {
        Header: "Patamar",
        accessor: "patamar",
        width: 220,
        minWidth: 190,
      },
      {
        Header: "Vendedor",
        accessor: "vendedor",
        width: 180,
        minWidth: 160,
      },
      {
        Header: "QTD",
        accessor: "qtd",
        width: 95,
        minWidth: 85,
        align: "right",
        headerAlign: "right",
        Cell: ({ value }) => fmtNumero(value, 0),
      },
      {
        Header: "Euros",
        accessor: "euros",
        width: 110,
        minWidth: 100,
        align: "right",
        headerAlign: "right",
        Cell: ({ value }) => fmtNumero(value, 2),
      },
      {
        Header: "Litros",
        accessor: "litros",
        width: 110,
        minWidth: 100,
        align: "right",
        headerAlign: "right",
        Cell: ({ value }) => fmtNumero(value, 3),
      },
      {
        Header: "Cliente",
        accessor: "cliente",
        width: 250,
        minWidth: 220,
      },
      {
        Header: "Data",
        accessor: "faturadata",
        width: 110,
        minWidth: 105,
        Cell: ({ value }) => fmtData(value),
      },
      {
        Header: "Fatura",
        accessor: "faturanumero",
        width: 130,
        minWidth: 120,
      },
      {
        Header: "Material",
        accessor: "material",
        width: 340,
        minWidth: 280,
      },
      {
        Header: "Fabricante",
        accessor: "fabricante",
        width: 220,
        minWidth: 190,
      },
      {
        Header: "Família",
        accessor: "familia",
        width: 180,
        minWidth: 150,
      },
      {
        Header: "Marca",
        accessor: "marca",
        width: 160,
        minWidth: 140,
      },
      {
        Header: "Submarca",
        accessor: "submarca",
        width: 170,
        minWidth: 150,
      },
      {
        Header: "Embalagem",
        accessor: "embalagem",
        width: 150,
        minWidth: 130,
      },
      {
        Header: "Seg. Pot.",
        accessor: "segmentopotencial",
        width: 130,
        minWidth: 115,
      },
      {
        Header: "Seg. N1",
        accessor: "segmenton1",
        width: 150,
        minWidth: 130,
      },
      {
        Header: "Seg. N2",
        accessor: "segmenton2",
        width: 150,
        minWidth: 130,
      },
      {
        Header: "Seg. N3",
        accessor: "segmenton3",
        width: 150,
        minWidth: 130,
      },
      {
        Header: "Grupo",
        accessor: "grupo",
        width: 170,
        minWidth: 150,
      },
      {
        Header: "Bloq. Forn.",
        accessor: "refbloqueiofornecimento",
        width: 130,
        minWidth: 115,
      },
      {
        Header: "Bloq. Fat.",
        accessor: "refbloqueiofaturacao",
        width: 130,
        minWidth: 115,
      },
      {
        Header: "Bloq. Ordem",
        accessor: "refbloqueioordem",
        width: 130,
        minWidth: 115,
      },
    ],
    []
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      fullScreen={isMobile}
      PaperComponent={isMobile ? undefined : PaperComponent}
      PaperProps={{
        sx: {
          width: {
            xs: "100%",
            sm: "calc(100% - 32px)",
          },
          maxWidth: {
            xs: "100%",
            sm: "98vw",
          },
          maxHeight: {
            xs: "100%",
            sm: "94vh",
          },
        },
      }}
    >
      <DialogTitle
        className="draggable-objetivo-v2-faturas-title"
        sx={{
          cursor: {
            xs: "default",
            sm: "move",
          },
        }}
      >
        <MDTypography variant="h5">
          Vendas
          {detalhe?.objetivo?.nome ? ` — ${detalhe.objetivo.nome}` : ""}
        </MDTypography>

        {detalhe?.objetivo && (
          <MDTypography variant="button" color="text" display="block" mt={0.5}>
            {fmtData(detalhe.objetivo.dataini)}
            {" a "}
            {fmtData(detalhe.objetivo.datafim)}
          </MDTypography>
        )}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: {
            xs: 1,
            sm: 2,
          },
          py: {
            xs: 1,
            sm: 2,
          },
          overflow: "auto",
        }}
      >
        {loading ? (
          <MDBox py={4} display="flex" justifyContent="center">
            <CircularProgress />
          </MDBox>
        ) : (
          <>
            {detalhe?.totais && (
              <MDBox display="flex" flexWrap="wrap" gap={2} mb={2}>
                <MDTypography variant="button" color="text">
                  Clientes: <strong>{detalhe?.total_clientes ?? 0}</strong>
                </MDTypography>

                <MDTypography variant="button" color="text">
                  Linhas: <strong>{detalhe?.total_linhas ?? 0}</strong>
                </MDTypography>

                <MDTypography variant="button" color="text">
                  QTD: <strong>{fmtNumero(detalhe.totais.qtd, 0)}</strong>
                </MDTypography>

                <MDTypography variant="button" color="text">
                  Euros: <strong>{fmtNumero(detalhe.totais.euros, 2)}</strong>
                </MDTypography>

                <MDTypography variant="button" color="text">
                  Litros: <strong>{fmtNumero(detalhe.totais.litros, 3)}</strong>
                </MDTypography>
              </MDBox>
            )}

            <MDBox mb={2}>
              <MDTypography variant="h6" mb={1}>
                Clientes distintos ({clientes.length})
              </MDTypography>

              <ProjectTable
                tableKey="objetivos-v2-dashboard-faturas-clientes"
                version="v1"
                columns={columnsClientes}
                data={clientes}
                loading={loading}
                minWidth={650}
                maxHeight="220px"
                emptyMessage="Sem clientes para apresentar."
                showKeepFilters
              />
            </MDBox>

            <MDBox>
              <MDTypography variant="h6" mb={1}>
                Vendas ({vendas.length})
              </MDTypography>

              <ProjectTable
                tableKey="objetivos-v2-dashboard-faturas"
                version="v1"
                columns={columnsVendas}
                data={vendas}
                loading={loading}
                minWidth={3500}
                maxHeight={isMobile ? "52vh" : "48vh"}
                emptyMessage="Sem vendas para apresentar."
                showKeepFilters
              />
            </MDBox>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <MDButton color="secondary" onClick={onClose}>
          Fechar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
